import { useCallback, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { LogViewer, LogViewerSearch } from '@patternfly/react-log-viewer';
import dayjs from 'dayjs';
import { useImmer } from 'use-immer';
import {
  faBarsStaggered,
  faDownload,
  faExpand,
  faPause,
  faPlay,
} from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';

import { Job, KeysOf, OutletContext } from '@typings';
import { ISO_WITHOUT_ZONE_FORMAT } from '@constants';
import { getPlatformAppLog } from '@services/platformApps.services';
import { clusterContextSelector } from '@selectors';
import { useSelector } from '@hooks';
import { as, toastifyResponseError } from '@utils';
import { fieldVariants } from '@utils/variants';

import { Button, Icon } from '@components';
import { Layout } from '@components/Layouts';

import '@patternfly/react-core/dist/styles/base-no-reset.css';
import '../../styles/log-viewer.css';

type Container = string;

type State = {
  pause: boolean;
  fullScreen: boolean;
  textWrapped: boolean;
  currentItemCount: number;
  linesBehind: number;
  itemCount: number;
  log: string;
  data: string[];
  container: Container;
  buffer: string[];
  stream: string[];
  timerId: NodeJS.Timer | null;
};

const defaultState: State = {
  pause: false,
  fullScreen: false,
  textWrapped: true,
  currentItemCount: 0,
  linesBehind: 0,
  itemCount: 1,
  log: '',
  data: [],
  container: '',
  buffer: [],
  stream: [],
  timerId: null,
};

const ITEMS_PER_PAGE = 50;

export const PlatformAppsInstanceLogPage = () => {
  const cluster = useSelector(clusterContextSelector);

  const { monitoringUrl } = as.c(cluster);

  const {
    instance: { id: instanceId },
    setTabTitle,
  } = useOutletContext<OutletContext.PlatformAppsInstance>();

  const logViewerRef = useRef<HTMLElement>(null);
  const timerRef = useRef<NodeJS.Timer | null>(null);
  const [state, setImmerState] = useImmer<State>(defaultState);

  const setState = useCallback(
    <Key extends KeysOf<State>>(key: Key, value: State[Key]) => {
      setImmerState((state) => {
        state[key] = value;
      });
    },
    [setImmerState],
  );

  useEffect(() => {
    setTabTitle(Job.Tab.Overview);
  }, [setTabTitle]);

  useEffect(() => {
    if (state.data.length <= state.itemCount) {
      return;
    }

    const timerId = setInterval(() => {
      setImmerState((state) => {
        state.itemCount += Math.min(
          ITEMS_PER_PAGE,
          state.data.length - state.itemCount,
        );
      });
    }, 100);
    timerRef.current = timerId;

    return () => {
      clearInterval(timerId);
    };
  }, [state.data.length, setState, setImmerState, state.itemCount]);

  useEffect(() => {
    if (!timerRef.current || !state.data.length) {
      return;
    }

    if (state.itemCount <= state.data.length) {
      setState('buffer', state.data.slice(0, state.itemCount));
    }
  }, [state.itemCount, state.data, setState]);

  useEffect(() => {
    const getLogData = async () => {
      try {
        const since = dayjs()
          .subtract(1, 'day')
          .format(ISO_WITHOUT_ZONE_FORMAT);
        const response = await getPlatformAppLog({
          instanceId,
          monitoringUrl,
          since,
        });
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        try {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            // eslint-disable-next-line no-await-in-loop
            const { done, value } = await reader!.read();

            if (done) {
              break;
            }

            const decodedLog = decoder.decode(value, { stream: true });

            setImmerState((state) => {
              state.data.push(...decodedLog.split('\n'));
            });
          }
        } finally {
          /**
           * Ensure the reader is closed even in case of an error
           */
          reader!.releaseLock();
        }
      } catch (error) {
        toastifyResponseError(error);
      }
    };

    getLogData();
  }, [instanceId, monitoringUrl, setImmerState]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = document.fullscreenElement;

      setState('fullScreen', !!isFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [setState]);

  useEffect(() => {
    const [pause, buffer, currentItemCount] = [
      state.pause,
      state.buffer,
      state.currentItemCount,
    ];
    if (!pause && buffer.length > 0) {
      setImmerState((state) => {
        state.currentItemCount = buffer.length;
        state.log = buffer.join('\n');
      });

      // @ts-expect-error todo: fix type error
      logViewerRef.current?.scrollToBottom();
    } else if (buffer.length !== currentItemCount) {
      setState('linesBehind', buffer.length - currentItemCount);
    } else {
      setState('linesBehind', defaultState.linesBehind);
    }
  }, [
    state.pause,
    state.buffer,
    state.currentItemCount,
    setImmerState,
    setState,
  ]);

  const handleExpandClick = () => {
    const isFullScreen = !state.fullScreen;
    const element = document.querySelector('#log-viewer');

    if (!element) {
      return;
    }

    if (isFullScreen) {
      element?.requestFullscreen();
    } else {
      document?.exitFullscreen();
    }

    setState('fullScreen', !isFullScreen);
  };

  type HandleScrollArgs = {
    scrollOffsetToBottom: number;
    scrollUpdateWasRequested: boolean;
  };
  const handleScroll = useCallback(
    ({ scrollOffsetToBottom, scrollUpdateWasRequested }: HandleScrollArgs) => {
      if (!scrollUpdateWasRequested) {
        setState('pause', scrollOffsetToBottom > 0);
      }
    },
    [setState],
  );

  const handleDownloadClick = () => {
    const { data } = state;

    const element = document.createElement('a');
    const file = new Blob(data, { type: 'text/plain' });

    element.href = URL.createObjectURL(file);
    element.download = `log-${instanceId}.txt`;

    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  };

  const renderPauseControl = () => {
    const { pause: isPaused, linesBehind } = state;
    const icon = isPaused ? faPlay : faPause;
    const linesBehindText =
      linesBehind === 0 ? '' : `and show ${linesBehind} lines`;
    const text = isPaused ? `Resume Log ${linesBehindText}` : 'Pause Log';

    return (
      <Button
        variant="ghost"
        onClick={() => setState('pause', !state.pause)}
        icon={icon}
        className="shrink-0"
      >
        {text}
      </Button>
    );
  };

  return (
    <Layout.Content className="relative px-8 py-4">
      <LogViewer
        // @ts-expect-error todo: describe error of component
        id="log-viewer"
        theme="light"
        className={clsx({
          'bg-white p-6 full-screen': state.fullScreen,
          'list-item-unwrapped': !state.textWrapped,
        })}
        data={state.log}
        isTextWrapped={state.textWrapped}
        scrollToRow={state.currentItemCount}
        height={state.fullScreen ? '100%' : '70vh'}
        innerRef={logViewerRef}
        toolbar={
          <div className="mb-4 flex items-center gap-6">
            <LogViewerSearch
              placeholder="Search"
              className={fieldVariants({ variant: 'static' })}
              minSearchChars={3}
              onFocus={() => setState('pause', true)}
            />
            {renderPauseControl()}
            <Button
              theme
              onClick={() => setState('textWrapped', !state.textWrapped)}
              className={clsx('ml-auto', { 'text-primary': state.textWrapped })}
            >
              <Icon icon={faBarsStaggered} className="text-[20px]" />
            </Button>
            <Button theme onClick={handleDownloadClick}>
              <Icon icon={faDownload} className="text-[20px]" />
            </Button>
            <Button theme onClick={handleExpandClick}>
              <Icon icon={faExpand} className="text-[20px]" />
            </Button>
          </div>
        }
        overScanCount={10}
        onScroll={handleScroll}
      />
    </Layout.Content>
  );
};
